<template>
  <div v-if="!loading && membershipPlanSelected">
    <div class="confirm-plan-change-wrapper d-flex flex-column flex-lg-row mx-auto">
      <div>
        <CancelFlowRedesignExperiment>
          <template #default>
            <plan-card
              :form-disabled="formDisabled"
              :plan="membershipPlanSelected"
              show-change-plan-button
              class="mx-auto mt-xl"
              header-text="Current Plan"
              @change-click="onChangePlanClick"/>
          </template>
          <template #redesign>
            <MembershipPlanCard
              :plan="membershipPlanSelected"
              :selectable="false"
              :show-payment-options="false"/>
          </template>
        </CancelFlowRedesignExperiment>
      </div>
      <div
        class="bg-white border flex-grow-1 ms-lg-xxl mt-xl mt-lg-0 fit-content-height p-4">
        <div>
          <h4 class="group-header">
            Payment Method
          </h4>
          <div
            class="d-flex justify-content-between bg-white pt-3 small-text">
            <DefaultPayment
              class="ms-0"
              :show-link="false"/>
            <text-link
              :disabled="formDisabled"
              @click="openModal({ name: 'update-credit-card' })">
              change
            </text-link>
          </div>
        </div>
        <change-plan-summary
          class="px-2"
          :form-disabled="formDisabled"
          fine-print-text="Confirm">
          <div
            v-if="stripeErrorMessage"
            class="text-center">
            <p class="small-text text-danger">
              {{ stripeErrorMessage }}
              <br><span v-html="stripeErrorFollowup"/>
            </p>
          </div>
          <GiftCardPromoCodeInput
            v-if="promoCodeEligible"
            v-model="enteredCode"
            class="pb-md"
            location="Confirm Plan Change"
            :disabled="formDisabled"
            @apply="apply"/>
          <div
            v-for="(error, index) in errors"
            :key="index"
            class="mt-2">
            <span
              class="small-text text-danger"
              v-html="error"/>
          </div>
          <SubscribeConsent
            v-model="consent"
            v-model:initials="consentInitials"/>
          <sequin-button
            id="btn-stripe"
            :disabled="confirmButtonDisabled"
            block
            class="mt-2 d-flex justify-content-center"
            @click="submitChangePlan">
            Confirm
          </sequin-button>
        </change-plan-summary>
      </div>
    </div>
  </div>
</template>

<script setup>
import GiftCardPromoCodeInput from '@/components/global/GiftCardPromoCodeInput.vue'
import PlanCard from '../../subscribe/PlanCard'
import DefaultPayment from '../../global/stripe/DefaultPayment.vue'
import ChangePlanSummary from '../ChangePlanSummary'
import SequinButton from '../../global/sequin/SequinButton'
import TextLink from '../../global/sequin/TextLink'
import MembershipPlanCard from '@/components/account/membership/new/MembershipPlanCard.vue'
import CancelFlowRedesignExperiment from '@/components/account/membership/new/CancelFlowRedesignExperiment.vue'
import usePayment from '@shared/composables/payment.js'
import { storeToRefs } from 'pinia'
import useAnalytics from '@shared/composables/analytics.js'
import { captureException } from '@sentry/vue'
import { useOverlaysStore } from '@/stores/overlays.js'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useAccountStore } from '@/stores/account.js'
import { useSubscriptionStore } from '@/stores/subscribe.js'
import { useClientStore } from '@shared/stores/client.js'
import { useRoute, useRouter } from 'vue-router'
import SubscribeConsent from '@/components/signUp/SubscribeConsent.vue'

const { track } = useAnalytics()
const { promoAmount, total } = usePayment()

const enteredCode = ref('')
const codes = ref([])
const errors = ref([])
const stripeErrorMessage = ref(null)
const stripeErrorFollowup = ref(null)
const paymentRequest = ref(null)
const formDisabled = ref(false)
const loading = ref(true)
const consent = ref(false)
const consentInitials = ref('')

const { breadcrumbs, showPlanUpdateSuccess } = storeToRefs(useAccountStore())
const subscriptionStore = useSubscriptionStore()
const { membershipPlans, membershipPlanSelected, membershipPlanQuotes, membershipPlanSelectedId } = storeToRefs(subscriptionStore)
const { fetchPlans, getQuote, changePlan } = subscriptionStore
const clientStore = useClientStore()
const { membershipDetails } = storeToRefs(clientStore)
const { updateMembershipPlan } = clientStore
const { openModal } = useOverlaysStore()

const confirmButtonDisabled = computed(() => formDisabled.value || !consent.value)
const promoCodeEligible = computed(() => membershipDetails.value?.planChangePromoEligible === true)
const planQuote = computed(() => membershipPlanQuotes.value[membershipPlanSelected.value?.id])

watch(promoAmount, () => {
  if (paymentRequest.value) {
    paymentRequest.value.update({
      total: {
        label: 'Armoire subscription',
        amount: Math.round(total.value * 100)
      }
    })
  }
})

const route = useRoute()

onMounted(async () => {
  if (breadcrumbs.value.length === 0) {
    // direct page load / browser refresh
    // breadcrumbs not important enough to save in query string, so fake
    // it. It's good enough.
    breadcrumbs.value = [
      { name: 'Membership', to: { name: 'membership-overview' } },
      { name: 'Confirm Plan Change' }
    ]
  }

  const query = route.query
  if (!membershipPlans.value) {
    // direct page load / browser refresh.  This data was not
    // loaded via the plan select page, so we have to do it here.
    await fetchPlans()
  }
  if (query?.planId) {
    membershipPlanSelectedId.value = query.planId
  }
  await nextTick()
  if (membershipPlanSelected.value?.id && !membershipPlanQuotes.value[membershipPlanSelected.value.id]) {
    try {
      await getQuote({ planId: membershipPlanSelected.value.id })
    } catch (err) {
      captureException(err, {
        extra: {
          planId: membershipPlanSelected.value.id,
          planChargeInterval: membershipPlanSelected.value.chargeInterval,
          errorMessage: 'Failed to fetch quote - line items may not display'
        }
      })
    }
  }

  const index = breadcrumbs.value.length > 1 ? breadcrumbs.value.length - 2 : breadcrumbs.value.length - 1
  track('Viewed Confirm Plan Change', {
    source: breadcrumbs.value[index].name,
    currentPlan: membershipPlans.value.currentBasePlan?.displayName,
    plan: membershipPlanSelected.value?.displayName,
    quote: planQuote.value
  })

  loading.value = false
})

const router = useRouter()
function onChangePlanClick() {
  router.go(-1)
}

async function apply () {
  formDisabled.value = true
  const code = enteredCode.value.toUpperCase()
  const codesToValidate = [...codes.value]
  errors.value = []
  if (!codes.value.includes(code)) {
    codesToValidate.push(code)
  }
  try {
    await getQuote({
      planId: membershipPlanSelected.value.id,
      codes: codesToValidate
    })
    codes.value = [...codesToValidate]
  } catch (err) {
    errors.value = Array.isArray(err) ? err : [err]
  } finally {
    enteredCode.value = ''
    formDisabled.value = false
  }
}

function onPlanChangeSuccess () {
  updateMembershipPlan(membershipPlanSelected.value)
}

async function submitChangePlan (onComplete = null) {
  formDisabled.value = true
  errors.value = []

  try {
    const data = {
      analyticsMetrics: {
        source: breadcrumbs.value[breadcrumbs.value.length - 2].name,
        quote: planQuote.value
      },
      promoCodes: codes.value,
      consentInitials: consentInitials.value
    }
    const res = await changePlan({ planId: membershipPlanSelected.value.id, data: data })

    if ('stripeError' in res.data) {
      stripeErrorMessage.value = res.data.stripeError.errorMessage
      stripeErrorFollowup.value = res.data.stripeError.errorFollowup

      if (onComplete) {
        onComplete.complete('fail')
      }
    } else {
      if (onComplete) {
        onComplete.complete('success')
      }
      try {
        onPlanChangeSuccess()
      } catch (err) {
        captureException(err)
      } finally {
        showPlanUpdateSuccess.value = true
        router.push({ name: 'membership-overview' })
      }
    }
  } catch (err) {
    errors.value = err || ['There was an issue confirming your subscription. Please try again.']
  } finally {
    formDisabled.value = false
  }
}
</script>

<style lang="scss" scoped>
.confirm-plan-change-wrapper {
  max-width: 1070px;
}

.sticky-top {
  top: 16px;
  height: fit-content;
}

.apple-pay-button {
  background-color: black;
  width: 100%;
  text-align: right;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI' !important;
  font-weight: $font-weight-semibold !important;

  &:hover {
    background-color: black;
  }

  .apple-pay-logo {
    background-image: -webkit-named-image(apple-pay-logo-white);
    background-size: 100% 100%;
    background-origin: content-box;
    background-repeat: no-repeat;
    height: 20px;
    width: 52px;
    margin-left: 4px;
  }
}
</style>
